import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Clock, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEuro = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;
const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }) + ' Uhr';
  } catch { return iso; }
};

/**
 * Gruppierter Rubellos-Verlauf (Tresor-Stil): eine Meldung = ein Mitarbeiter-Submit mit allen Losen.
 * Pro Los: getippte Zahl A→B, verkauft, Betrag. Pro Meldung: "übernommen von" + Gesamtsumme.
 */
export default function LottoVerlaufGrouped({ startDate = '', endDate = '', locationId = '', refreshKey = 0, showSummary = true }) {
  const [data, setData] = useState({ meldungen: [], summary_per_employee: [], total_sold: 0, total_revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [cardOpen, setCardOpen] = useState(true);
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let s = startDate, e = endDate;
      if (!s || !e) {
        const today = new Date();
        s = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        e = today.toISOString().split('T')[0];
      }
      const params = new URLSearchParams({ start_date: s, end_date: e });
      if (locationId) params.append('location_id', locationId);
      const res = await axios.get(`${API}/lotto/report-history-grouped?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || { meldungen: [], summary_per_employee: [], total_sold: 0, total_revenue: 0 });
    } catch (err) {
      setData({ meldungen: [], summary_per_employee: [], total_sold: 0, total_revenue: 0 });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, locationId, refreshKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const { meldungen, summary_per_employee } = data;

  return (
    <div className="space-y-3" data-testid="lotto-verlauf-grouped">
      {/* Zusammenfassung pro Mitarbeiter */}
      {showSummary && summary_per_employee.length > 0 && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl divide-y divide-gray-100 dark:divide-gray-800">
          {summary_per_employee.map((s) => (
            <div key={s.employee_id} className="flex items-center justify-between px-4 sm:px-5 py-3" data-testid={`lotto-summary-${s.employee_id}`}>
              <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{s.employee_name}</span>
              <div className="flex items-center gap-4 flex-shrink-0 text-sm">
                <span className="text-gray-400 text-xs">{s.meldungen} Meldungen</span>
                <span className="text-emerald-600 dark:text-emerald-400 font-medium">{s.sold} verkauft</span>
                <span className="font-bold text-gray-900 dark:text-white w-20 text-right">{fmtEuro(s.revenue)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Verlauf – pro Meldung gruppiert */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button
          onClick={() => setCardOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          data-testid="lotto-verlauf-toggle"
        >
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Verlauf ({meldungen.length})</span>
          </div>
          {cardOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>

        {cardOpen && (
          loading ? (
            <p className="px-5 py-6 text-sm text-gray-500 text-center border-t border-gray-100 dark:border-gray-800">Lädt…</p>
          ) : meldungen.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-500 text-center border-t border-gray-100 dark:border-gray-800">Noch keine Meldungen in diesem Zeitraum.</p>
          ) : (
            <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
              {meldungen.map((m) => {
                const isOpen = !!expanded[m.id];
                return (
                  <div key={m.id} data-testid={`lotto-meldung-${m.id}`}>
                    {/* Meldungs-Kopf */}
                    <button
                      onClick={() => toggle(m.id)}
                      className="w-full flex items-start gap-3 px-4 sm:px-5 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                      data-testid={`lotto-meldung-toggle-${m.id}`}
                    >
                      {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-gray-900 dark:text-white">{m.reporter_name}</span>
                          {m.uebernommen_von && (
                            <span className="text-xs text-amber-600 dark:text-amber-400">übernommen von: {m.uebernommen_von}</span>
                          )}
                        </div>
                        <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                          {m.total_sold} verkauft · {fmtEuro(m.total_revenue)}
                        </span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-gray-400">{fmtDateTime(m.reported_at)}</p>
                      </div>
                    </button>

                    {/* Lose dieser Meldung */}
                    {isOpen && (
                      <div className="bg-gray-50/70 dark:bg-gray-800/30 px-4 sm:px-5 pb-3 pt-1">
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                          {m.items.map((it, idx) => (
                            <div key={idx} className="flex items-center gap-3 py-2" data-testid={`lotto-item-${m.id}-${idx}`}>
                              <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{it.product_name}</span>
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 flex-shrink-0">
                                {it.from} <ArrowRight className="h-3 w-3" /> {it.to}
                              </span>
                              <span className={`text-xs font-semibold flex-shrink-0 w-32 text-right ${it.sold > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
                                verkauft {it.sold} · {fmtEuro(it.revenue)}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-200 dark:border-gray-700">
                          <span className="text-sm font-bold text-gray-900 dark:text-white">Gesamt</span>
                          <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{m.total_sold} verkauft · {fmtEuro(m.total_revenue)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}
